import React from 'react';
import { TEAM_LABELS } from '../../config';
import { Badge, Button, Card, Check, Chip, Field, Input, SearchBox, Segmented, Select } from '../../ui/basics';
import MultiSelect from '../../ui/MultiSelect';

export const PERIODS = ['All Time', 'Today', 'Last 7 Days', 'Last 30 Days', 'Last Week', 'Current Week', 'Last Month', 'Current Month',
  'Last Quarter', 'Current Quarter', 'Last Year', 'Current Year', 'Custom'];
export const BASIS = [{ value: 'started', label: 'Started' }, { value: 'ended', label: 'Ended' },
  { value: 'both', label: 'Both' }, { value: 'either', label: 'Either' }];
const BASIS_HINT = {
  started: 'Placements whose start date is in the period.',
  ended: 'Placements whose end date is in the period.',
  both: 'Placements that started and also ended in the period.',
  either: 'Placements that started or ended in the period.',
};

export const DEFAULTS = { period: 'Last Month', from: '', to: '', basis: 'started', leads: [], clients: [], recruiters: [], locations: [], status: '', search: '',
  early: false, earlyMin: '', earlyMax: '' };

/** The report builder form. `filters` is the whole state; `onChange` receives a partial update. */
export default function ReportFilters({ filters, onChange, facets, teamKeys, fixedTeam, invalid, summary }) {
  const allTime = filters.period === 'All Time';
  const toggleTeam = (t) => onChange({ leads: filters.leads.includes(t) ? filters.leads.filter((x) => x !== t) : [...filters.leads, t] });
  const changed = JSON.stringify(filters) !== JSON.stringify(DEFAULTS);
  const earlyError = filters.early && filters.earlyMin && filters.earlyMax && Number(filters.earlyMin) > Number(filters.earlyMax) ? 'The minimum is above the maximum.' : '';

  return (
    <Card title="Build your report" subtitle="Combine any filters. Results, totals and exports all follow what you choose here."
      actions={changed && <Button variant="ghost" size="sm" onClick={() => onChange({ ...DEFAULTS })}>Reset filters</Button>}>
      <div className="stack">
        <div className="row" style={{ alignItems: 'flex-end', gap: 16 }}>
          <Field label="Period"><Select style={{ width: 170 }} value={filters.period} options={PERIODS} onChange={(e) => onChange({ period: e.target.value })} /></Field>
          {filters.period === 'Custom' && <>
            <Field label="From"><Input type="date" style={{ width: 160 }} invalid={Boolean(invalid)} value={filters.from} onChange={(e) => onChange({ from: e.target.value })} /></Field>
            <Field label="To"><Input type="date" style={{ width: 160 }} invalid={Boolean(invalid)} min={filters.from || undefined} value={filters.to} onChange={(e) => onChange({ to: e.target.value })} /></Field>
          </>}
          <Field label="Placements that…" hint={allTime ? 'Choose a period to filter by start or end date.' : BASIS_HINT[filters.basis]}>
            <div style={{ opacity: allTime ? 0.5 : 1, pointerEvents: allTime ? 'none' : 'auto' }}>
              <Segmented options={BASIS} value={filters.basis} onChange={(basis) => onChange({ basis })} />
            </div>
          </Field>
        </div>
        {invalid && <div className="alert warning">{invalid}</div>}

        <div className="grid cols-4" style={{ alignItems: 'start' }}>
          <Field label="Team">
            {fixedTeam ? <Badge tone="accent">{TEAM_LABELS[fixedTeam]} only</Badge> : (
              <div className="row" style={{ gap: 6 }}>
                <Chip on={filters.leads.length === 0} onClick={() => onChange({ leads: [] })}>All</Chip>
                {teamKeys.map((t) => <Chip key={t} on={filters.leads.includes(t)} onClick={() => toggleTeam(t)}>{TEAM_LABELS[t]}</Chip>)}
              </div>)}
          </Field>
          <Field label="Client">
            <MultiSelect options={facets ? facets.clients : []} value={filters.clients} onChange={(clients) => onChange({ clients })} placeholder="All clients" noun="clients" ariaLabel="Clients" />
          </Field>
          <Field label="Recruiter">
            <MultiSelect options={facets ? facets.recruiters : []} value={filters.recruiters} onChange={(recruiters) => onChange({ recruiters })} placeholder="All recruiters" noun="recruiters" ariaLabel="Recruiters" />
          </Field>
          <Field label="Status">
            <Select value={filters.status} options={['Active', 'Inactive']} placeholder="Any status" onChange={(e) => onChange({ status: e.target.value })} />
          </Field>
        </div>
        <div className="grid cols-4" style={{ alignItems: 'start' }}>
          <Field label="Location">
            <MultiSelect options={facets ? facets.locations || [] : []} value={filters.locations} onChange={(locations) => onChange({ locations })} placeholder="All locations" noun="locations" ariaLabel="Locations" />
          </Field>
          <Field label="Early ends" hint="Ended before the projected end date.">
            <Check label="Only placements that ended early" checked={filters.early} onChange={(e) => onChange({ early: e.target.checked, ...(e.target.checked ? {} : { earlyMin: '', earlyMax: '' }) })} />
          </Field>
          {filters.early && <>
            <Field label="At least (days early)" error={earlyError}><Input type="number" min="1" placeholder="Any" invalid={Boolean(earlyError)} value={filters.earlyMin} onChange={(e) => onChange({ earlyMin: e.target.value })} aria-label="Minimum days early" /></Field>
            <Field label="At most (days early)"><Input type="number" min="1" placeholder="Any" value={filters.earlyMax} onChange={(e) => onChange({ earlyMax: e.target.value })} aria-label="Maximum days early" /></Field>
          </>}
        </div>
        <SearchBox value={filters.search} onChange={(search) => onChange({ search })} placeholder="Also match a candidate name…" />
        {summary && <p className="muted">{summary}</p>}
      </div>
    </Card>
  );
}
